'use client'

import { create, type StateCreator } from 'zustand'
import { devtools, persist } from 'zustand/middleware'

import { PERMISSION } from '@/lib/auth/permissions'
import { hasPermission } from '@/lib/auth/permissions-utils'

import { Admin } from './auth-types'

type AdminStore = {
  admin: Admin | null
  adminPermissions: PERMISSION[]
  clearAdmin: () => void
  hasPermission: (permission: PERMISSION) => boolean
  isAdminLoading: boolean
  setAdmin: (admin: Admin | null) => void
  setAdminPermissions: (permissions: PERMISSION[]) => void
  setIsAdminLoading: (isAdminLoading: boolean) => void
}

const createAdminStore: StateCreator<
  AdminStore,
  [['zustand/devtools', never], ['zustand/persist', unknown]]
> = (set, get) => ({
  admin: null,
  adminPermissions: [],
  clearAdmin: () =>
    set(
      {
        admin: null,
        adminPermissions: [],
        isAdminLoading: false
      },
      false,
      'clearAdmin'
    ),
  hasPermission: (permission: PERMISSION) => {
    const { adminPermissions } = get()
    return hasPermission(adminPermissions, permission)
  },
  isAdminLoading: false,
  setAdmin: (admin: Admin | null) => set({ admin }, false, 'setAdmin'),
  setAdminPermissions: (permissions: PERMISSION[]) =>
    set({ adminPermissions: permissions }, false, 'setAdminPermissions'),
  setIsAdminLoading: (isAdminLoading: boolean) =>
    set({ isAdminLoading }, false, 'setIsAdminLoading')
})

export const useAdminStore = create<AdminStore>()(
  devtools(
    persist(createAdminStore, {
      name: 'adminStore',
      partialize: (state) => ({
        admin: state.admin,
        adminPermissions: state.adminPermissions
      })
    }),
    { name: 'adminStore' }
  )
)
